import type { UniverseId } from '@/domain/universes/universe.interface';
import { arraysContainSameElements } from '@/utils/toolkit/array/arrays-contain-same-elements.utils';
import { AsyncPipe } from '@angular/common';
import { Component, type OnInit, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import type { Building } from '../../../interfaces/Building.interface';
import type { CoreSensor } from '../../../interfaces/Sensor.interface';
import { BuildingsProvider } from '../../../providers/buildings/buildings.provider';
import { DashboardingProvider } from '../../../providers/dashboarding.provider';
import { SensorsProvider } from '../../../providers/sensors/sensors.provider';
import { WidgetSize } from '../../../providers/widgets.contants';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { SensorInfoComponent } from '../../sensors/sensor-info/sensor-info';
import { AbstractWidgetClass } from '../AbstractWidget.class';

interface SensorsUniversesInformation {
    universeId: UniverseId;
    sensors: CoreSensor[];
}

@Component({
    styleUrls: ['./widget-home-fav-sensors.scss'],
    selector: 'widget-home-fav-sensors',
    templateUrl: 'widget-home-fav-sensors.html',
    imports: [SensorInfoComponent, LoadingAndErrorComponent, AsyncPipe, TranslatePipe]
})
export class WidgetHomeFavSensorsComponent extends AbstractWidgetClass<null, void> implements OnInit {
    private _buildingsProvider = inject(BuildingsProvider);
    private _sensorsProvider = inject(SensorsProvider);
    private _dashboardingProvider = inject(DashboardingProvider);

    size = WidgetSize.FULL;

    isMonoBuilding$!: Observable<boolean>;
    building$!: Observable<Building>;

    loading$!: Observable<boolean>;
    sensorsUniverses$!: Observable<SensorsUniversesInformation[]>;

    ngOnInit() {
        this.isMonoBuilding$ = this._dashboardingProvider.getIsMonoBuilding$();
        this.building$ = this._dashboardingProvider.getMeaningfulBuildingId$().pipe(
            switchMap(buildingId => this._buildingsProvider.getBuilding$(buildingId)),
            shareReplay({ refCount: true, bufferSize: 1 })
        );

        this.loading$ = this._sensorsProvider.getIsSensorStatePending$();

        this.sensorsUniverses$ = this._dashboardingProvider.getMeaningfulBuildingIds$().pipe(
            switchMap(buildingIds => this._sensorsProvider.getSensorsByBuildingIds$(buildingIds)),
            distinctUntilChanged(arraysContainSameElements),
            map(coreSensors => {
                const sensorUniversesMap = this._sensorsProvider.getCoreSensorMapByUniverse(coreSensors);
                const sensorUniversesInformation: SensorsUniversesInformation[] = [];
                sensorUniversesMap.forEach((sensors, universeId) => {
                    sensorUniversesInformation.push({
                        universeId,
                        sensors
                    });
                });
                return sensorUniversesInformation;
            }),
            shareReplay({ refCount: true, bufferSize: 1 })
        );
    }
}
